/**
 * The match-events log's model — PD-4.
 *
 * Pure functions, no React, for the reason `components/conversation/trace.ts`
 * records: the thing this package is judged on is which links a row offers and
 * in what order rows appear, and both are computable without a DOM.
 *
 * ## Three stores, one row
 *
 * A row is a `/match_events` node joined to its D1 ingest-ledger entry:
 *
 *   request node   `/match_events/requests/{pebbleId}_{storyVersion}` — the
 *                  submit the executor committed. Carries the pebble, the
 *                  version, the story text and `extracted` (RTDB-keyed).
 *   result node    `/match_events/results/{…}__{kind}` — what the matcher
 *                  decided. Carries `payload.match_id` / `payload.match_ids` /
 *                  `payload.session_id` and, on a request-FUL node, the
 *                  `request_key` that caused it.
 *   ledger row     `match_event_requests` (migration 029) — the ONLY store that
 *                  knows the `story_id` a request produced, plus the outcome,
 *                  the latency and the error.
 *
 * The join is the request key, and it is not decorative: the story link on a
 * row exists ONLY because of it, and PB-9's sweep GCs `done` nodes after N days
 * while the ledger row stays. So a ledger row with no surviving node is still a
 * row here (`nodeMissing`) — the alternative is a log that quietly forgets
 * every submit older than the GC window.
 *
 * ## Nothing is reshaped
 *
 * `node` is the bytes as stored, every row has a raw disclosure next to it, and
 * every field read goes through a tolerant narrowing helper (`str`/`num`/`obj`,
 * reused from the conversation model): a screen whose whole job is to show a
 * BROKEN pebble must render a malformed node rather than refuse to draw it.
 *
 * `extracted` is the one exception, and it is the exception PA-18, PD-1 and
 * PD-2 all named as this package's job: it is STORED RTDB-keyed (`them:name`)
 * because RTDB refuses a `.` in a key, and it is decoded HERE, in the view
 * layer, with the vendored `contract/extracted_v1.rtdb-keys.json`. The stored
 * form stays reachable in the raw disclosure, so the screen still diffs against
 * a trigger payload.
 */
import { arr, num, obj, str } from '../conversation/trace';
import type { Tone } from '../conversation/ui';
import RTDB_KEYS from '../../contract/extracted_v1.rtdb-keys.json';

// ---------------------------------------------------------------------------
// extracted_v1 key decoding
// ---------------------------------------------------------------------------

/** `them:name` → `them.name`, built once from the vendored contract snapshot. */
const RTDB_TO_DOT: ReadonlyMap<string, string> = new Map(
  Object.entries(RTDB_KEYS.keys as Record<string, string>).map(([dot, rtdb]) => [rtdb, dot]),
);

/**
 * Decode one stored `extracted` map for display.
 *
 * A key the contract does not know is passed through UNTOUCHED rather than
 * mechanically re-keyed: the matcher's ingest counts it as `unknown_key`, and a
 * screen that renamed it would show the operator a key no store holds and no
 * boundary accepted. Same rule as the matcher's own `decodeExtractedFromRtdb`.
 */
export function decodeExtracted(raw: unknown): Record<string, unknown> | null {
  const map = obj(raw);
  if (map === null) return null;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(map)) {
    out[RTDB_TO_DOT.get(key) ?? key] = value;
  }
  return out;
}

/** True when at least one key is still in storage form — i.e. decoding did
 *  something, which is worth saying on screen next to the decoded map. */
export function wasRtdbKeyed(raw: unknown): boolean {
  const map = obj(raw);
  if (map === null) return false;
  return Object.keys(map).some((k) => RTDB_TO_DOT.has(k));
}

// ---------------------------------------------------------------------------
// The payload
// ---------------------------------------------------------------------------

/** `GET /admin/match-events`'s `data`. Every node is passed through unvalidated
 *  by the route, and stays `unknown` here for the same reason PD-1's payload
 *  does. */
export interface MatchEventsLog {
  pebble_id: string | null;
  limit: number;
  requests: unknown[];
  results: unknown[];
  ledger: Record<string, unknown>[];
  sources: Record<string, { status: string; error?: string; detail?: string; count?: number }>;
  degraded: boolean;
}

/** One `match_event_requests` row, narrowed. */
export interface LedgerView {
  requestKey: string;
  outcome: string | null;
  storyId: string | null;
  resultKind: string | null;
  bestScore: number | null;
  error: string | null;
  claimedAt: number | null;
  completedAt: number | null;
  latencyMs: number | null;
  source: string | null;
  storyVersion: number | null;
  pebbleId: string | null;
}

export function viewLedger(raw: unknown): LedgerView | null {
  const r = obj(raw);
  const requestKey = str(r?.request_key);
  if (r === null || requestKey === null) return null;
  return {
    requestKey,
    outcome: str(r.outcome),
    storyId: str(r.story_id),
    resultKind: str(r.result_kind),
    bestScore: num(r.best_score),
    error: str(r.error),
    claimedAt: num(r.claimed_at),
    completedAt: num(r.completed_at),
    latencyMs: num(r.latency_ms),
    source: str(r.source),
    storyVersion: num(r.story_version),
    pebbleId: str(r.pebble_id),
  };
}

// ---------------------------------------------------------------------------
// Links — the acceptance, as data
// ---------------------------------------------------------------------------

/**
 * Where a row can take you. Every one of these is the acceptance criterion
 * "from a match-events row the match, story and refinement pages open" plus
 * PD-2's timeline, expressed as a value so a test can assert it without a DOM.
 *
 * `refinement` is `/matches/{id}?tab=refinement` rather than a route of its own:
 * `RefinementSessionCard` is rendered inside `MatchDetailPage`'s refinement tab
 * and there is no standalone refinement page in this admin. A session with no
 * match id therefore has no refinement link — a link that lands nowhere is
 * worse than no link.
 */
export interface RowLinks {
  timeline: string | null;
  story: string | null;
  match: string | null;
  refinement: string | null;
}

export function conversationPath(pebbleId: string, params: Record<string, string> = {}): string {
  const qs = new URLSearchParams(params).toString();
  return `/conversation/${encodeURIComponent(pebbleId)}${qs ? `?${qs}` : ''}`;
}

export function matchEventsPath(pebbleId: string, params: Record<string, string> = {}): string {
  const qs = new URLSearchParams(params).toString();
  return `/match-events/${encodeURIComponent(pebbleId)}${qs ? `?${qs}` : ''}`;
}

export function refinementPath(matchId: string): string {
  return `/matches/${encodeURIComponent(matchId)}?tab=refinement`;
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

export interface MatchEventRow {
  /** React key. Unique across sides and across ledger-only rows — a collision
   *  makes React reuse another row's DOM, which on a table with a REPLAY button
   *  means the button under the operator's cursor belongs to a different node. */
  id: string;
  side: 'request' | 'result';
  /** The RTDB node key, or the ledger's `request_key` when the node is gone. */
  key: string;
  /** The provenance join. A request node's own key; a result node's field. */
  requestKey: string | null;
  pebbleId: string | null;
  storyVersion: number | null;
  ts: number | null;
  /** Request: the delivery status. Result: the contract `kind`. */
  label: string;
  source: string | null;
  clientEvent: string | null;
  mustTellUser: boolean | null;
  attempts: number | null;
  error: string | null;
  /** Request-LESS results only (`payload.origin`): the mechanism that caused it. */
  origin: string | null;
  originId: string | null;
  noChangeReason: string | null;
  payload: Record<string, unknown> | null;
  ledger: LedgerView | null;
  links: RowLinks;
  /** `extracted` decoded out of storage form — request rows only. */
  extracted: Record<string, unknown> | null;
  extractedWasRtdbKeyed: boolean;
  /** The bytes, as stored. Empty when only the ledger survives. */
  node: Record<string, unknown>;
  nodeMissing: boolean;
  /** A replay needs a stored node to re-post; a GC'd one cannot be replayed. */
  replayable: boolean;
}

function pebbleFromKey(key: string): string | null {
  const cut = key.lastIndexOf('_');
  if (cut <= 0) return null;
  const head = key.slice(0, cut);
  return /^[1-9]\d*$/.test(key.slice(cut + 1)) ? head : null;
}

function linksFor(input: {
  pebbleId: string | null;
  ledger: LedgerView | null;
  payload: Record<string, unknown> | null;
}): RowLinks {
  const matchId =
    str(input.payload?.match_id) ??
    (arr(input.payload?.match_ids).map((m) => str(m)).find((m): m is string => m !== null) ?? null);
  const sessionId = str(input.payload?.session_id);
  return {
    timeline: input.pebbleId === null ? null : conversationPath(input.pebbleId),
    story: input.ledger?.storyId ? `/stories/${encodeURIComponent(input.ledger.storyId)}` : null,
    match: matchId === null ? null : `/matches/${encodeURIComponent(matchId)}`,
    refinement: sessionId !== null && matchId !== null ? refinementPath(matchId) : null,
  };
}

function requestRow(raw: unknown, ledgerByKey: Map<string, LedgerView>): MatchEventRow | null {
  const n = obj(raw);
  if (n === null) return null;
  const key = str(n.key) ?? '(no key)';
  const ledger = ledgerByKey.get(key) ?? null;
  const pebbleId = str(n.pebble_id) ?? ledger?.pebbleId ?? pebbleFromKey(key);
  return {
    id: `request:${key}`,
    side: 'request',
    key,
    requestKey: str(n.key),
    pebbleId,
    storyVersion: num(n.story_version) ?? ledger?.storyVersion ?? null,
    ts: num(n.ts) ?? ledger?.claimedAt ?? null,
    label: str(n.status) ?? 'unknown',
    source: str(n.source) ?? ledger?.source ?? null,
    clientEvent: null,
    mustTellUser: null,
    attempts: num(n.attempts),
    error: str(n.error) ?? ledger?.error ?? null,
    origin: null,
    originId: null,
    noChangeReason: null,
    payload: null,
    ledger,
    // A request node carries no match/session of its own; its links come from
    // the ledger (story) and its own pebble (timeline). The match a submit
    // eventually produced is on the RESULT row, where it was decided.
    links: linksFor({ pebbleId, ledger, payload: null }),
    extracted: decodeExtracted(n.extracted),
    extractedWasRtdbKeyed: wasRtdbKeyed(n.extracted),
    node: n,
    nodeMissing: false,
    replayable: str(n.key) !== null && pebbleFromKey(key) !== null,
  };
}

function resultRow(raw: unknown, ledgerByKey: Map<string, LedgerView>): MatchEventRow | null {
  const n = obj(raw);
  if (n === null) return null;
  const key = str(n.key) ?? '(no key)';
  const requestKey = str(n.request_key);
  const ledger = requestKey === null ? null : (ledgerByKey.get(requestKey) ?? null);
  const payload = obj(n.payload);
  const pebbleId = str(n.pebble_id) ?? ledger?.pebbleId ?? null;
  return {
    id: `result:${key}`,
    side: 'result',
    key,
    requestKey,
    pebbleId,
    storyVersion: ledger?.storyVersion ?? null,
    ts: num(n.ts),
    label: str(n.kind) ?? 'unknown',
    source: null,
    clientEvent: str(n.client_event),
    mustTellUser: typeof n.must_tell_user === 'boolean' ? n.must_tell_user : null,
    attempts: null,
    error: null,
    origin: str(payload?.origin),
    originId: str(payload?.origin_id),
    noChangeReason: str(payload?.no_change_reason),
    payload,
    ledger,
    links: linksFor({ pebbleId, ledger, payload }),
    extracted: null,
    extractedWasRtdbKeyed: false,
    node: n,
    nodeMissing: false,
    replayable: false,
  };
}

/** A ledger row whose node PB-9's GC has already removed. Real history, and the
 *  only place a submit older than the GC window survives. */
function ledgerOnlyRow(l: LedgerView): MatchEventRow {
  const pebbleId = l.pebbleId ?? pebbleFromKey(l.requestKey);
  return {
    id: `ledger:${l.requestKey}`,
    side: 'request',
    key: l.requestKey,
    requestKey: l.requestKey,
    pebbleId,
    storyVersion: l.storyVersion,
    ts: l.completedAt ?? l.claimedAt,
    label: l.outcome ?? 'unknown',
    source: l.source,
    clientEvent: null,
    mustTellUser: null,
    attempts: null,
    error: l.error,
    origin: null,
    originId: null,
    noChangeReason: null,
    payload: null,
    ledger: l,
    links: linksFor({ pebbleId, ledger: l, payload: null }),
    extracted: null,
    extractedWasRtdbKeyed: false,
    node: {},
    nodeMissing: true,
    replayable: false,
  };
}

/**
 * Every row of the log, newest first.
 *
 * Undated rows sort LAST rather than first: an absent `ts` is missing
 * information, and putting it at the head would claim it is the newest thing
 * that happened. Ties keep their input order (requests before results before
 * ledger-only), so a request and the result it caused at the same millisecond
 * read in causal order.
 */
export function buildRows(log: MatchEventsLog | null): MatchEventRow[] {
  if (log === null) return [];
  const ledgerViews = log.ledger.map(viewLedger).filter((l): l is LedgerView => l !== null);
  const ledgerByKey = new Map(ledgerViews.map((l) => [l.requestKey, l]));

  const requests = log.requests.flatMap((r) => {
    const row = requestRow(r, ledgerByKey);
    return row === null ? [] : [row];
  });
  const results = log.results.flatMap((r) => {
    const row = resultRow(r, ledgerByKey);
    return row === null ? [] : [row];
  });

  const nodeKeys = new Set(requests.map((r) => r.key));
  const orphans = ledgerViews.filter((l) => !nodeKeys.has(l.requestKey)).map(ledgerOnlyRow);

  const rows = [...requests, ...results, ...orphans];
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      if (a.row.ts === null && b.row.ts === null) return a.index - b.index;
      if (a.row.ts === null) return 1;
      if (b.row.ts === null) return -1;
      if (a.row.ts !== b.row.ts) return b.row.ts - a.row.ts;
      return a.index - b.index;
    })
    .map((e) => e.row);
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/**
 * Delivery status → tone. Unknown statuses fall back to gray with the raw text
 * as the label, exactly as `NotifyLogTable`'s reason badge does: a status a
 * later migration adds lands in the table without breaking it.
 */
export const REQUEST_TONE: Record<string, Tone> = {
  pending: 'amber',
  processing: 'blue',
  done: 'green',
  failed: 'red',
  // ledger-only rows carry the ingest outcome as their label
  in_progress: 'blue',
  created: 'green',
  updated: 'green',
  no_change: 'gray',
};

/** Result `kind` → tone. The eleven closed kinds, plus a gray fallback. */
export const KIND_TONE: Record<string, Tone> = {
  no_change: 'gray',
  questions_ready: 'amber',
  waiting_other_side: 'blue',
  rematching: 'blue',
  match_found: 'green',
  consent_waiting: 'blue',
  match_confirmed: 'green',
  match_rejected: 'red',
  match_failed: 'red',
  pebble_expired: 'gray',
  passive_match_found: 'green',
};

export function toneFor(row: MatchEventRow): Tone {
  return (row.side === 'request' ? REQUEST_TONE[row.label] : KIND_TONE[row.label]) ?? 'gray';
}

/** Date AND time, unlike the conversation timeline's `formatClock`: this log
 *  spans days, and a bare wall clock on a row from last Tuesday misleads. */
export function formatStamp(ts: number | null): string {
  if (ts === null) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
